import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
  Chip,
  Paper,
  Alert
} from '@mui/material';
import {
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { auth } from '../../../../../firebase';
import { getApiBase, API_ENDPOINTS } from '../../../../../utils/apiConfig';

const SchoolSearchInput = ({ 
  value, 
  onChange, 
  disabled = false, 
  label = "Search School by Name",
  placeholder = "Type school name to search...",
  helperText = "Search for schools in your teams"
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Clear search when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const performSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const authHeaders = await getAuthHeaders();
      
      // DEVELOPMENT OVERRIDE: Force school search to use local backend
      // since the new search endpoint only exists locally during development
      const isLocalDevelopment = process.env.NODE_ENV === 'development' || 
                                window.location.hostname === 'localhost' ||
                                window.location.hostname === '127.0.0.1';
      
      let apiUrl;
      if (isLocalDevelopment || process.env.REACT_APP_FORCE_LOCAL_SEARCH) {
        // Use local backend for development or when explicitly forced
        apiUrl = `http://localhost:5001/api${API_ENDPOINTS.CLOUD_RUN.SEARCH}?q=${encodeURIComponent(query)}&limit=20`;
      } else {
        // Use standard environment routing for production
        apiUrl = `${getApiBase()}${API_ENDPOINTS.CLOUD_RUN.SEARCH}?q=${encodeURIComponent(query)}&limit=20`;
      }
      
      // Debug logging (can be removed in production)
      // console.log('School search URL:', apiUrl);
      // console.log('Auth headers:', Object.keys(authHeaders));
      // console.log('Environment check - isDev:', isLocalDevelopment, 'NODE_ENV:', process.env.NODE_ENV);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        credentials: 'include'
      });

      console.log('Search response status:', response.status);
      console.log('Search response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `Search failed: ${response.status} ${response.statusText}`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (jsonError) {
            console.error('Failed to parse error response as JSON:', jsonError);
          }
        } else {
          // Handle non-JSON responses
          const responseText = await response.text();
          console.error('Non-JSON error response:', responseText.substring(0, 200));
          
          if (responseText.includes('<!doctype') || responseText.includes('<html')) {
            errorMessage = `Server returned HTML instead of JSON. This usually indicates a routing problem. Status: ${response.status}`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Search results:', data);
      setSearchResults(data.schools || []);
      
    } catch (error) {
      console.error('School search failed:', error);
      setSearchError(error.message);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const getAuthHeaders = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user found');
    }
    
    try {
      const token = await currentUser.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw new Error('Authentication failed');
    }
  };

  const handleInputChange = (event, newValue, reason) => {
    if (reason === 'input') {
      setSearchQuery(newValue);
      
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Debounce search by 300ms
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(newValue);
      }, 300);
    }
  };

  const handleSelectionChange = (event, newValue) => {
    if (newValue) {
      // Call the parent onChange with the selected school data
      onChange({
        uuid: newValue.uuid,
        ncessch: newValue.ncessch,
        name: newValue.name,
        district: newValue.district,
        state: newValue.state,
        team: newValue.team
      });
    } else {
      onChange(null);
    }
  };

  const formatOptionLabel = (option) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
      <SchoolIcon color="primary" fontSize="small" />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" component="div">
          {option.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {option.district}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {option.state}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GroupIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {option.team}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label={`UUID: ${option.uuid}`}
            size="small"
            variant="outlined"
            sx={{ mr: 1, fontSize: '0.75rem' }}
          />
          <Chip
            label={`NCES: ${option.ncessch}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Autocomplete
        options={searchResults}
        getOptionLabel={(option) => option.name || ''}
        isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Paper component="li" key={key} {...otherProps} elevation={0}>
              {formatOptionLabel(option)}
            </Paper>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={helperText}
            fullWidth
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        loading={isSearching}
        onInputChange={handleInputChange}
        onChange={handleSelectionChange}
        value={value}
        inputValue={searchQuery}
        filterOptions={(x) => x} // Don't filter on frontend, we do it on backend
        noOptionsText={
          searchQuery.length < 2 
            ? "Type at least 2 characters to search"
            : isSearching 
              ? "Searching..." 
              : "No schools found"
        }
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        freeSolo={false}
        disabled={disabled}
      />
      
      {searchError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {searchError}
        </Alert>
      )}
      
      {value && (
        <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Selected School:</strong>
          </Typography>
          <Typography variant="body2">
            <strong>Name:</strong> {value.name}
          </Typography>
          <Typography variant="body2">
            <strong>UUID:</strong> {value.uuid}
          </Typography>
          <Typography variant="body2">
            <strong>NCES ID:</strong> {value.ncessch}
          </Typography>
          <Typography variant="body2">
            <strong>District:</strong> {value.district}
          </Typography>
          <Typography variant="body2">
            <strong>Team:</strong> {value.team}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SchoolSearchInput;
